import { mediaUrl, trpc, type Catalog } from '@tg/app-core';
import { blockNote, dayHas, isRest, linkLabel, type Block, type Exercise, type Line, type Maxes, type WorkoutContent } from '@tg/domain';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { D, MONTHS, RU, addDays, hhmm, monday } from '../lib/dates';
import { fmtN, initials, pmName, useCatalog, useMaxes, useMe, useToday } from '../lib/data';
import { setActive, setSelDate } from '../lib/nav';
import { lsSet, useLocal } from '../lib/storage';
import { useWakeLock } from '../lib/wake';
import { blockPmKeys, blockTech, blockTitle, chainParts, clientLine, freeze, lineTech, num, segments, visibleBlocks, type LinePlan, type TechItem } from '../lib/workout';
import { enqueue } from '../ui/achQueue';
import { BackIcon, I } from '../ui/icons';
import { Ask, Sheet, toast } from '../ui/overlay';
import { ResTab, resEntries, resValue, type Fact } from '../ui/ResTab';
import { RestIcon, Tabs, useBack } from '../ui/parts';
import { useDay } from './Home';

type DayData = NonNullable<ReturnType<typeof useDay>['data']>;
type Msg = DayData['messages'][number];
type Res = DayData['results'][number];

/* T1. Тренировка дня, как её составил тренер. Проценты клиент не видит —
   только рабочий вес от своих текущих максимумов (CON-16). */
export function Workout() {
  const { date = '' } = useParams();
  const nav = useNavigate();
  const goBack = useBack();
  const today = useToday();
  const me = useMe();
  const cat = useCatalog();
  const { pm } = useMaxes();
  const day = useDay(date);
  const utils = trpc.useUtils();
  const [calOpen, setCalOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useLocal(`msg:${date}`, true);

  const settings = (me.data?.settings || {}) as Record<string, unknown>;
  useWakeLock(settings.awake !== false);

  const content = day.data?.content as WorkoutContent | null | undefined;
  const blocks = visibleBlocks(content);
  const dayDone = !!day.data?.done.includes('');
  /* Тренировка открыта сегодня — при возврате в приложение откроется она (CLI-6).
     Ушли на другой день, не закончив сегодняшнюю, — снимаем отметку: иначе при
     возврате из фона приложение перекидывало бы обратно на сегодня поверх того
     дня, который открыли осознанно. */
  useEffect(() => {
    if (date === today && blocks.length && !dayDone) setActive(date);
    else if (date && date !== today) setActive(null);
  }, [date, today, blocks.length, dayDone]);
  useEffect(() => setSelDate(date), [date]);

  const patch = (f: (d: DayData) => DayData) => utils.clientApp.day.setData({ date }, (d) => (d ? f(d as DayData) : d) as never);
  const toggle = trpc.clientApp.toggleBlock.useMutation({ onSettled: () => utils.clientApp.day.invalidate({ date }) });
  const finish = trpc.clientApp.finishDay.useMutation({
    onSuccess: (r) => {
      /* Очередь (ACH-2): сначала «первые» ачивки, потом срыв серии (из прошлого пропуска), потом момент
         серии, если она выросла — ничего из этого нет, просто тост, как раньше */
      r.firstAch?.forEach((id) => enqueue({ kind: 'ach', id }));
      if (r.lost) enqueue({ kind: 'lost', n: r.lost.n, best: r.lost.best, quote: r.lost.quote });
      if (r.streak) enqueue({ kind: 'streak', ...r.streak });
      if (!r.firstAch?.length && !r.lost && !r.streak) toast('Тренировка завершена');
    },
    onSettled: () => {
      utils.clientApp.day.invalidate({ date });
      utils.clientApp.calendar.invalidate();
      utils.clientApp.me.invalidate();
    },
  });

  /* Снять завершение — только с подтверждением: случайный тап по зелёной кнопке
     не должен делать выполненную тренировку незавершённой */
  const [undo, setUndo] = useState<(() => void) | null>(null);
  /* Пока день не загружен с сервера, отметки по заглушке не меняем */
  const loading = day.isPlaceholderData;
  /* Кружок блока — только его собственная отметка: «Завершить тренировку» не отмечает пропущенные блоки */
  const onRing = (b: Block) => {
    if (loading) return;
    const done = !!day.data?.done.includes(b.id);
    patch((d) => ({ ...d, done: done ? d.done.filter((x) => x !== b.id) : [...d.done, b.id] }));
    toggle.mutate({ date, blockId: b.id, done: !done });
  };
  const onFinish = () => {
    if (!content || loading) return;
    if (dayDone) {
      setUndo(() => () => {
        patch((d) => ({ ...d, done: d.done.filter((x) => x !== '') }));
        finish.mutate({ date, done: false });
      });
      return;
    }
    /* Снимок с посчитанными весами — новый 1ПМ историю не перепишет */
    const snapshot = day.data?.frozen ? content : freeze(content, pm, cat.ex);
    patch((d) => ({ ...d, done: [...d.done, ''] }));
    finish.mutate({ date, snapshot, done: true });
    setActive(null);
    nav('/');
  };

  /* Комментарии ко всей тренировке — так тренер отвечает на завершённую тренировку из ленты */
  const dayMsgs = (day.data?.messages || []).filter((m) => m.scope === 'day');
  const dayUnread = dayMsgs.some((m) => m.author === 'trainer' && !m.readAt);
  const readMsgs = trpc.clientApp.readMessages.useMutation();
  useEffect(() => {
    if (!dayUnread || loading) return;
    patch((d) => ({ ...d, messages: d.messages.map((m) => (m.scope === 'day' && m.author === 'trainer' && !m.readAt ? { ...m, readAt: new Date() } : m)) }));
    readMsgs.mutate({ date, scope: 'day' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayUnread, loading, date]);
  const tr = me.data?.trainer;
  const trName = tr ? [tr.first, tr.last].filter(Boolean).join(' ') : 'Тренер';

  const dl = D(date);
  const dateTx = dl.getDate() + ' ' + MONTHS[dl.getMonth()] + ' ' + dl.getFullYear();
  const mon = monday(date);
  const cal = calOpen ? (
    <div className="card wcal">
      <button className="ib sm" onClick={() => nav('/day/' + addDays(date, -7), { replace: true })} aria-label="Прошлая неделя">
        <I n="left" />
      </button>
      <div className="wcd">
        {Array.from({ length: 7 }, (_, k) => {
          const d = addDays(mon, k);
          return (
            <button key={d} className={d === date ? 'on' : ''} onClick={() => nav('/day/' + d, { replace: true })}>
              <s>{RU[k]}</s>
              <b>{D(d).getDate()}</b>
              <i />
            </button>
          );
        })}
      </div>
      <button className="ib sm" onClick={() => nav('/day/' + addDays(date, 7), { replace: true })} aria-label="Следующая неделя">
        <I n="right" />
      </button>
    </div>
  ) : null;
  const head = (
    <header className="top">
      <button className="ib" onClick={goBack} aria-label="Назад">
        <BackIcon />
      </button>
      {/* Без названия — «Тренировка»; «Отдых» — только если тренировки в этот день нет */}
      <div className="t caps">{content?.title || (day.isPending ? '' : content && dayHas(content) ? 'Тренировка' : 'Отдых')}</div>
      <button className={'ib acc' + (calOpen ? ' on' : '')} onClick={() => setCalOpen(!calOpen)} aria-label="Календарь" aria-expanded={calOpen}>
        <I n="cal" />
      </button>
    </header>
  );

  if (!day.data && day.isPending)
    return (
      <>
        {head}
        <div className="pg wk">{cal}</div>
        <Tabs on="home" />
      </>
    );

  if (!blocks.length)
    return (
      <>
        {head}
        <div className="pg wk">
          {cal}
          <p className="wdate">{dateTx}</p>
          <div className="card empty">
            {content?.comp ? <I n="trophy" /> : <RestIcon />}
            <b>{content?.comp ? content.title : 'День отдыха'}</b>
            <span>{content?.comp ? 'Соревнование — тренер не расписывал тренировку' : 'Тренировки нет — восстанавливайтесь'}</span>
          </div>
        </div>
        <Tabs on="home" />
      </>
    );

  return (
    <>
      {head}
      <div className="pg wk">
        {cal}
        <p className="wdate">{dateTx}</p>
        {content?.message ? (
          <div className={'card vrow' + (msgOpen ? ' on' : '')}>
            <button className="vh" onClick={() => setMsgOpen(!msgOpen)} aria-expanded={msgOpen}>
              <I n={msgOpen ? 'down' : 'right'} />
              <span>Сообщение тренера</span>
            </button>
            {msgOpen ? (
              <div className="vb">
                <p>{content.message}</p>
              </div>
            ) : null}
          </div>
        ) : null}
        {dayMsgs.length ? (
          <div className="card vrow on">
            <div className="vh">
              <I n="msg" />
              <span>Комментарий тренера</span>
            </div>
            <div className="vb">
              <div className="thr">
                {dayMsgs.map((m) =>
                  m.author === 'trainer' ? (
                    <div className="msgt" key={m.id}>
                      <span className="av">{tr ? initials(tr.first, tr.last) : ''}</span>
                      <div className="bub">
                        <s>{trName}</s>
                        <p>{m.text}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="msgc" key={m.id}>
                      <div className="bub">
                        <p>{m.text}</p>
                        <s>{hhmm(m.createdAt as unknown as string)}</s>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        ) : null}
        {blocks.map((b) => (
          <BlockView
            key={b.id}
            b={b}
            date={date}
            day={day.data!}
            pm={pm}
            cat={cat}
            done={day.data!.done.includes(b.id)}
            missed={dayDone && !day.data!.done.includes(b.id)}
            onRing={() => onRing(b)}
            patch={patch}
          />
        ))}
        <button className={'cday' + (dayDone ? ' ok' : '')} onClick={onFinish}>
          {dayDone ? (
            <>
              <I n="check" />
              Тренировка завершена
            </>
          ) : (
            'Завершить тренировку'
          )}
        </button>
      </div>
      <Ask
        open={!!undo}
        onClose={() => setUndo(null)}
        title="Снять отметку?"
        text="Тренировка станет незавершённой. Отметки блоков и записанные результаты останутся."
        label="Снять отметку"
        onOk={() => {
          undo?.();
          setUndo(null);
        }}
      />
      <Tabs on="home" />
    </>
  );
}

interface BlockProps {
  b: Block;
  date: string;
  day: DayData;
  pm: Maxes;
  cat: Catalog;
  done: boolean;
  /** Тренировка завершена, а этот блок не отмечен — красный крестик */
  missed: boolean;
  onRing: () => void;
  patch: (f: (d: DayData) => DayData) => void;
}

/** Короткие подписи вкладок: иконка и слово в строку влезают в четверть 360 px */
const TAB_SHORT: Record<string, string> = { chat: 'Чат', tech: 'Техника', res: 'Итог', rec: 'ПМ' };

function BlockView({ b, date, day, pm, cat, done, missed, onRing, patch }: BlockProps) {
  const k = `${date}:${b.id}`;
  const [collapsed, setCollapsed] = useLocal(`bc:${k}`, false);
  const [tab, setTab] = useLocal<string>(`tab:${k}`, '');
  const me = useMe();
  const utils = trpc.useUtils();
  const readMsgs = trpc.clientApp.readMessages.useMutation();
  const save = trpc.clientApp.saveResult.useMutation({ onSettled: () => utils.clientApp.day.invalidate({ date }) });

  const exs = useMemo(() => blockTech(b, cat.ex), [b, cat.ex]);
  const pmKeys = useMemo(() => blockPmKeys(b, cat.ex), [b, cat.ex]);
  const msgs = day.messages.filter((m) => m.scope === 'block' && m.refId === b.id);
  const unread = msgs.some((m) => m.author === 'trainer' && !m.readAt);
  /* Точка — есть сообщение тренера, которое клиент ещё не открывал. Комментарий к блоку — не сообщение: он виден под заголовком */
  const dot = unread;
  const resOf = (lineId: string) => day.results.find((r: Res) => r.blockId === b.id && r.lineId === lineId);
  /* Вкладки (прототип): Чат · Техника · Итог · ПМ — короткие подписи, полное название — для VoiceOver */
  const rows: [string, string, string][] = [['chat', 'Обсуждение', 'msg']];
  if (exs.length) rows.push(['tech', 'Техника', 'video']);
  const ents = resEntries(b, day.frozen ? null : pm, cat);
  if (ents.length) rows.push(['res', 'Результат', 'clip']);
  if (pmKeys.length) rows.push(['rec', 'Новый 1ПМ', 'trophy']);
  /* «Итог» оранжевый, когда записано всё */
  const got = ents.length > 0 && ents.every((e) => !!resValue(e, (resOf(e.lineId)?.fact as Fact) || null));
  /* Тап по строке упражнения (или комплекса) — «Итог» открывается на ней */
  const canRes = new Set(ents.map((e) => e.lineId));
  const [focusRes, setFocusRes] = useState<{ id: string; n: number } | null>(null);
  const openRes = (id: string) => {
    setTab('res');
    setFocusRes({ id, n: Date.now() });
  };
  const cur = rows.some((r) => r[0] === tab) ? tab : '';

  const onTab = (id: string) => {
    setTab(cur === id ? '' : id);
    if (id === 'chat') {
      if (unread) {
        patch((d) => ({ ...d, messages: d.messages.map((m) => (m.scope === 'block' && m.refId === b.id && m.author === 'trainer' ? { ...m, readAt: new Date() } : m)) }));
        readMsgs.mutate({ date, scope: 'block', refId: b.id });
      }
    }
  };

  const saveRes = (lineId: string, exId: string | null, plan: Record<string, unknown> | null, fact: Fact) => {
    patch((d) => {
      const rest = d.results.filter((r) => !(r.blockId === b.id && r.lineId === lineId));
      return { ...d, results: [...rest, { ...(resOf(lineId) || {}), blockId: b.id, lineId, fact, plan } as Res] };
    });
    save.mutate({ date, blockId: b.id, lineId, exId, plan, fact });
  };
  const planSnap = (p: LinePlan) => ({ text: p.text, sets: p.sets, reps: p.reps, kg: p.kg, unit: p.unit });

  const segs = segments(b);
  const tr = me.data?.trainer;
  const trName = tr ? [tr.first, tr.last].filter(Boolean).join(' ') : 'Тренер';

  return (
    <section className={'blk' + (done ? ' done' : '')}>
      <div className="bh">
        <button className="bt" onClick={() => setCollapsed(!collapsed)} aria-expanded={!collapsed}>
          <b>{blockTitle(b)}</b>
          <I n="down" cls={collapsed ? '' : 'up'} />
        </button>
        <button className={'ring' + (done ? ' on' : missed ? ' miss' : '')} onClick={onRing} aria-label={done ? 'Снять отметку' : missed ? 'Не выполнен — отметить выполненным' : 'Отметить блок выполненным'}>
          {done ? <I n="check" /> : missed ? <I n="x" /> : null}
        </button>
      </div>
      {collapsed ? null : (
        <div className="card bcard">
          {blockNote(b) ? (
            <p className="bnote" title="Комментарий тренера к блоку">
              <I n="msg" />
              <span>{blockNote(b)}</span>
            </p>
          ) : null}
          <div className="segs">
            {segs.map((seg, si) => (
              <div className="seg" key={si}>
                {seg.map((r, ri) => {
                  if (r.k !== 'line') return <div className="xl" key={ri}>{r.text}</div>;
                  return <LineRow key={r.line.id} line={r.line} pm={pm} cat={cat} frozen={day.frozen} onTap={canRes.has(r.line.id) ? () => openRes(r.line.id) : undefined} />;
                })}
              </div>
            ))}
          </div>
          <div className="sect row">
            {rows.map(([id, t, ic]) => (
              <button key={id} className={(cur === id ? 'on' : '') + (id === 'res' && got ? ' got' : '')} onClick={() => onTab(id)} aria-expanded={cur === id} aria-label={t}>
                <span className="sic">
                  <I n={ic} />
                  {id === 'chat' && dot ? <i className="dot" aria-label="новое" /> : null}
                </span>
                <span className="lb">{TAB_SHORT[id]}</span>
              </button>
            ))}
          </div>
          {cur === 'chat' ? <Chat b={b} date={date} msgs={msgs} trName={trName} trIni={tr ? initials(tr.first, tr.last) : ''} patch={patch} /> : null}
          {cur === 'tech' ? <Tech exs={exs} /> : null}
          {cur === 'res' ? (
            <ResTab
              ents={ents}
              focus={focusRes}
              onAuto={(e, fact) => saveRes(e.lineId, e.exId, e.plan ? planSnap(e.plan) : null, fact)}
              factOf={(id) => (resOf(id)?.fact as Fact) || null}
              onSave={(list) => {
                for (const { e, fact } of list) saveRes(e.lineId, e.exId, e.fmt ? { fmt: e.fmt } : e.plan ? planSnap(e.plan) : null, fact);
                if (!done) onRing();
                toast('Результат записан');
              }}
            />
          ) : null}
          {cur === 'rec' ? <Record keys={pmKeys} pm={pm} cat={cat} /> : null}
        </div>
      )}
    </section>
  );
}

/* Строка упражнения: «3×5 Становая тяга 132,5 кг»; связка — одной строкой,
   вес с подходами не разрываются. Тап открывает «Итог» блока на этой строке — там подходы. */
function LineRow({ line, pm, cat, frozen, onTap }: { line: Line; pm: Maxes; cat: Catalog; frozen: boolean; onTap?: () => void }) {
  /* Отдых между упражнениями — просто строка со временем: результат к нему не записывают */
  if (isRest(line))
    return (
      <div className="xl xrest">
        <I n="rest" />
        {clientLine(line, null, cat.ex)}
      </div>
    );
  const ch = chainParts(line, frozen ? null : pm, cat.ex);
  const vid = lineTech(line, cat.ex);
  const [vOpen, setVOpen] = useState(false);
  return (
    <div>
      <div className={'xl' + (onTap ? ' tap' : '')} onClick={onTap}>
        {vid ? (
          <button
            className="xvid"
            aria-label="Видео тренера"
            onClick={(e) => {
              e.stopPropagation();
              setVOpen(true);
            }}
          >
            <I n="play" />
          </button>
        ) : null}
        {ch ? (
          <>
            {ch.names}
            {ch.prm ? (
              <>
                {' '}
                <span className="nw">{ch.prm}</span>
              </>
            ) : null}
          </>
        ) : (
          clientLine(line, frozen ? null : pm, cat.ex)
        )}
      </div>
      {vid ? <TechSheet it={vid} open={vOpen} onClose={() => setVOpen(false)} /> : null}
    </div>
  );
}

/* Обсуждение блока: заметка тренера первым сообщением, клиент справа,
   внизу поле. Неотправленный текст — черновик на устройстве. */
function Chat({ b, date, msgs, trName, trIni, patch }: { b: Block; date: string; msgs: Msg[]; trName: string; trIni: string; patch: (f: (d: DayData) => DayData) => void }) {
  const key = `draft:${date}:${b.id}`;
  const [draft, setDraft] = useLocal(key, '');
  const utils = trpc.useUtils();
  const send = trpc.clientApp.send.useMutation({ onSettled: () => utils.clientApp.day.invalidate({ date }) });
  const go = async () => {
    const text = draft.trim();
    if (!text) return;
    const tmp = { id: 'tmp' + Date.now(), scope: 'block', refId: b.id, author: 'client', text, createdAt: new Date(), readAt: null } as unknown as Msg;
    patch((d) => ({ ...d, messages: [...d.messages, tmp] }));
    setDraft('');
    try {
      await send.mutateAsync({ date, scope: 'block', refId: b.id, text });
    } catch (e) {
      patch((d) => ({ ...d, messages: d.messages.filter((m) => m.id !== tmp.id) }));
      lsSet(key, text);
      toast(e instanceof Error && /тренер/i.test(e.message) ? 'Сообщения появятся, когда вас пригласит тренер' : 'Не отправилось — нет связи. Текст сохранён');
    }
  };
  const has = msgs.length > 0;
  return (
    <div className="rb">
      {has ? (
        <div className="thr">
          {msgs.map((m) =>
            m.author === 'trainer' ? (
              <div className="msgt" key={m.id}>
                <span className="av">{trIni}</span>
                <div className="bub">
                  <s>{trName}</s>
                  <p>{m.text}</p>
                </div>
              </div>
            ) : (
              <div className="msgc" key={m.id}>
                <div className="bub">
                  <p>{m.text}</p>
                  <s>{hhmm(m.createdAt as unknown as string)}</s>
                </div>
              </div>
            ),
          )}
        </div>
      ) : null}
      <div className="rin">
        <textarea className="inp ta" rows={2} placeholder="Веса, повторы, время, вопрос тренеру…" value={draft} onChange={(e) => setDraft(e.target.value)} />
        <button className="snd" onClick={go} aria-label="Отправить тренеру" disabled={!draft.trim()}>
          <I n="send" />
        </button>
      </div>
    </div>
  );
}

/* Техника: превью упражнений блока, тап — шторка с демонстрацией и шагами */
function Tech({ exs }: { exs: TechItem[] }) {
  const [sel, setSel] = useState<TechItem | null>(null);
  const [open, setOpen] = useState(false);
  return (
    <div className="rb">
      <div className="demos">
        {exs.map((e) => (
          <button
            className="demo"
            key={e.key}
            onClick={() => {
              setSel(e);
              setOpen(true);
            }}
          >
            <span className={'th' + (e.video || e.link ? ' own' : '')}>
              {e.ex?.img ? <img src={mediaUrl(e.ex.img)} alt="" loading="lazy" /> : <em>{e.title.slice(0, 1)}</em>}
              <i>
                <I n="play" />
              </i>
              {e.video || e.link ? <b className="vtag">Видео</b> : null}
            </span>
            <span className="dn">{e.title}</span>
          </button>
        ))}
      </div>
      <TechSheet it={sel} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

/* Шторка техники: видео тренера и демонстрация базы — обе, если тренер их не убрал; ниже шаги и акценты */
function TechSheet({ it, open, onClose }: { it: TechItem | null; open: boolean; onClose: () => void }) {
  const e = it?.ex ?? null;
  const gif = e?.gif && !it?.noGif ? e.gif : null;
  const det = trpc.catalog.exerciseDetail.useQuery({ id: e?.id || '' }, { enabled: !!e && open, staleTime: Infinity });
  const steps = (det.data?.steps || []) as string[];
  const tips = det.data?.tips as unknown;
  const tipText = Array.isArray(tips) ? tips.join(' ') : typeof tips === 'string' ? tips : '';
  return (
    <Sheet open={open && !!it} onClose={onClose} label={it?.title || ''}>
      {it?.video ? (
        <div className="tvid">{open ? <iframe src={it.video.embed} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen title={it.title} /> : null}</div>
      ) : null}
      {it?.link ? (
        <a className="tlink" href={it.link.url} target="_blank" rel="noopener noreferrer">
          <I n="play" />
          {linkLabel(it.link)}
          <I n="ext" />
        </a>
      ) : null}
      {gif ? <img className="tgif" src={mediaUrl(gif)} alt="" /> : null}
      {it?.video || it?.link || gif ? null : (
        <div className="tgif none">
          <I n="play" />
          <span>Демонстрации пока нет</span>
        </div>
      )}
      <h2>{it?.title}</h2>
      {tipText ? <p>{tipText}</p> : null}
      {steps.length ? (
        <ol className="tsteps">
          {steps.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ol>
      ) : tipText || (e && det.isPending) || (!e && (it?.video || it?.link)) ? null : (
        <p>Описание техники тренер ещё не добавил.</p>
      )}
      <div className="acts">
        <button className="btn sec" onClick={onClose}>
          Понятно
        </button>
      </div>
    </Sheet>
  );
}

/* Рекорд прямо в тренировке: новый 1ПМ сразу пересчитывает веса этой и следующих */
function Record({ keys, pm, cat }: { keys: string[]; pm: Maxes; cat: Catalog }) {
  const [vals, setVals] = useState<Record<string, string>>({});
  const utils = trpc.useUtils();
  const today = useToday();
  const add = trpc.clientApp.addMax.useMutation({
    onSuccess: (r) => r.firstAch?.forEach((id) => enqueue({ kind: 'ach', id })),
    onSettled: () => utils.clientApp.maxes.invalidate(),
  });
  const set = (k: string) => {
    const v = num(vals[k] || '');
    if (!v) return;
    utils.clientApp.maxes.setData(undefined, (rows) => [...((rows || []) as never[]), { id: 'tmp' + k, key: k, kg: v, reps: 1, date: today, source: 'client' } as never]);
    add.mutate({ key: k, kg: v });
    setVals((x) => ({ ...x, [k]: '' }));
    toast('Рекорд записан — веса пересчитаны');
  };
  return (
    <div className="rb">
      {keys.map((k) => (
        <div className="recr" key={k}>
          <span>
            <b>{pmName(k, cat)}</b>
            <s>{pm[k] ? '1ПМ сейчас ' + fmtN(pm[k]) + ' кг' : '1ПМ ещё нет'}</s>
          </span>
          <label className="recin">
            <input
              inputMode="decimal"
              placeholder={pm[k] ? fmtN(pm[k]) : '1ПМ'}
              aria-label={'Новый 1ПМ, ' + pmName(k, cat)}
              value={vals[k] || ''}
              onChange={(e) => setVals((x) => ({ ...x, [k]: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && set(k)}
            />
            <i>кг</i>
          </label>
          <button className="snd recok" onClick={() => set(k)} aria-label="Записать рекорд" disabled={!num(vals[k] || '')}>
            <I n="check" />
          </button>
        </div>
      ))}
    </div>
  );
}
